import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion as m } from "framer-motion";
import PageHeader from "../components/PageHeader";
import ProjectCard from "../components/ProjectCard";
import ProjectDetails from "../components/ProjectDetails";
import { Container, Reveal, Section } from "../components/ui";
import { projectCategories, projects } from "../data/projects";

const Projects = () => {
  const [category, setCategory] = useState("All");
  const [detail, setDetail] = useState(null);

  const filtered = useMemo(
    () =>
      category === "All"
        ? projects
        : projects.filter((p) => p.category === category),
    [category]
  );

  const counts = useMemo(() => {
    const map = { All: projects.length };
    projects.forEach((p) => {
      map[p.category] = (map[p.category] || 0) + 1;
    });
    return map;
  }, []);

  return (
    <>
      <Helmet>
        <title>Work — Jephthah Boateng</title>
        <meta
          name="description"
          content="Production systems across platform engineering, data pipelines and product — each written up as a short case study."
        />
      </Helmet>

      <PageHeader
        eyebrow="Work"
        title="The systems I built, and why."
        lead="Platforms, pipelines and products. Every entry says what was broken, what I tried and what actually shipped — because the screenshots on their own prove very little."
        meta={[
          `${projects.length} projects`,
          "2021 — 2026",
          `${projectCategories.length - 1} disciplines`,
        ]}
      />

      {/* Filter bar */}
      <div className="sticky top-[68px] z-30 border-b border-[var(--color-border)] bg-paper/90 backdrop-blur-xl dark:bg-night/90">
        <Container>
          <div className="hide-scrollbar flex gap-1 overflow-x-auto py-4">
            {projectCategories.map((cat) => {
              const active = cat === category;
              return (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`relative shrink-0 whitespace-nowrap rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                    active
                      ? "text-paper dark:text-paper"
                      : "text-ink-muted hover:text-ink dark:text-slightwhite/55 dark:hover:text-slightwhite"
                  }`}
                >
                  {active && (
                    <m.span
                      layoutId="filter-pill"
                      className="absolute inset-0 rounded-full bg-azure dark:bg-electric"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                  <span className="relative">
                    {cat}
                    <span className="ml-2 opacity-60">{counts[cat] || 0}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Container>
      </div>

      <Section tone="default">
        <AnimatePresence mode="wait">
          <m.div
            key={category}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filtered.map((project, i) => (
              <ProjectCard
                key={project.slug}
                project={project}
                index={i}
                onOpen={setDetail}
              />
            ))}
          </m.div>
        </AnimatePresence>

        {filtered.length === 0 && (
          <Reveal>
            <p className="py-24 text-center font-display text-3xl text-ink-muted">
              Nothing filed under {category} yet.
            </p>
          </Reveal>
        )}
      </Section>

      <ProjectDetails detail={detail} setView={() => setDetail(null)} />
    </>
  );
};

export default Projects;
